import hit from "./axios";
import { handleHeader } from "./cookie";
import { retryUndelivered } from "./serverRetry";
import { persistOtpLength } from "./otpLength";
import { OTP_UPSTREAM_TIMEOUT_MS } from "./otpRequestTimeout";

const getHome = (params = {}, headers = {}) => hit({
  url: "api/web/v1/home",
  method: "GET",
  params,
  headers,
});

const getFitArticles = (params = {}, cookie = {}) => hit({
  url: "api/web/v1/page",
  method: "GET",
  params,
  headers: {
    ...cookie,
    ...handleHeader(cookie)
  },
});

const getArticleDetail = (id, params, cookie = {}) => hit({
  url: `api/web/v1/article/${id}`,
  method: "GET",
  params,
  headers: handleHeader(cookie),
});

const getCookingDetail = (id, params, headers = {}) => hit({
  url: `/api/v1/cooking-videos/${id}`,
  method: "GET",
  params,
  headers,
});

// Trello 437 V5: exchange a single-use app handoff code for the app's session.
// Called server-side only (from getInitialProps), so the token never rides in a URL.
const exchangeAppHandoffCode = (code = "") => hit({
  url: "api/v1/auth/app-handoff/exchange",
  method: "POST",
  data: { code },
});

const getContactUsCategories = (params = {}) => hit({
  url: "api/v1/contact-categories",
  method: "GET",
  params,
});

const getBusinessType = (params = {}) => hit({
  url: "api/v1/business/type-options",
  method: "GET",
  params,
});

const createContact = (data) => hit({
  url: "api/v1/contacts",
  method: "POST",
  data,
});

const getStaticContentDetail = (slug, params = {}) => hit({
  url: `api/web/v1/static-content/${slug}`,
  method: "GET",
  params,
});

const activateUser = (data, headers = {}) => hit({
  url: "oauth/activation/activate",
  method: "POST",
  data,
  headers
});

const confirmChangeEmail = (token, headers = {}) => hit({
  url: `oauth/member/change-email/confirm/${token}`,
  method: "PUT",
  headers
});

const submitResetPassword = (payload, headers = {}) => hit({
  url: "oauth/password/reset",
  method: "POST",
  data: payload,
  headers
});

const getArticleByCategories = (params, cookie = {}) => hit({
  url: "api/web/v1/article",
  method: "GET",
  params,
  headers: handleHeader(cookie),
});

const getListFood = (params) => hit({
  url: "/api/v1/new-fit-trackers/food-listing",
  method: "GET",
  params,
});

const getDetailFood = (params) => hit({
  url: "/api/v1/new-fit-trackers/food-detail",
  method: "GET",
  params,
});

const subscribeNews = (params) => hit({
  url: "/api/v1/subscribers",
  method: "POST",
  params,
});

const activateNewsLetter = (params, headers = {}) => hit({
  url: "api/v1/subscribers/activation",
  method: "POST",
  params,
  headers,
});

const unsubscribeNewsLetter = (token, data = {}, headers = {}) => hit({
  url: `/api/v1/unsubscribe/${token}`,
  method: "POST",
  data,
  headers
});

const getPackageDetail = (id, query = {}, headers = {}, ctx = null) => hit({
  url: `/api/v1/product-packages/${id}`,
  method: "GET",
  params: query,
  headers,
  ctx,
});

const submitPromoCode = (body, headers) => hit({
  url: "/api/v1/vouchers/apply",
  method: "PUT",
  data: body,
  headers,
});

// Checkout is the one call where a lost TCP connect costs a sale, so it retries
// when — and only when — the request provably never reached the backend. See
// helpers/serverRetry.js for why a delivered-but-unanswered request is never
// retried: that would mint a second invoice for the same buyer.
const checkoutPackage = (packageId, body, headers) => retryUndelivered(() => hit({
  url: `/api/v1/product-packages/${packageId}/checkout`,
  method: "POST",
  data: body,
  headers,
}));

// Trello 546: /xt lane — the same checkout, but through the backend route that
// marks the request for the payment orchestrator. Only the /xt page calls
// this; every other surface keeps the public checkout endpoint.
const checkoutPackageXt = (packageId, body, headers) => retryUndelivered(() => hit({
  url: `/api/v1/product-packages/${packageId}/xt-checkout`,
  method: "POST",
  data: body,
  headers,
}));

// Public product-package list (used server-side by /xt to offer the
// subscription packages testable through the orchestrator lane). ctx carries
// the visitor's CF country/IP headers — without it the backend resolves the
// list with no caller geo and may return another region's pricing.
const getProductPackages = (params = {}, headers = {}, ctx = null) => hit({
  url: "/api/v1/product-packages",
  method: "GET",
  params,
  headers,
  ctx,
});

const likeArticle = (data, cookie) => hit({
  url: "/api/v1/articles/like",
  method: "POST",
  data,
  headers: handleHeader(cookie),
});

const likeCookingVideos = (body = {}, cookie = {}) => hit({
  url: "api/v1/cooking-videos/like",
  method: "POST",
  data: body,
  headers: handleHeader(cookie),
});

const checkWebsite = (ctx = {}) => hit({
  url: "/api/v1/lock-website/lock_website",
  method: "GET",
  ctx
});

const getShareContent = (params = {}) => hit({
  url: "api/web/v1/share",
  method: "GET",
  params,
});

const getWorkoutLanding = (params = {}, headers = {}, ctx = null) => hit({
  url: "/api/web/v1/static-content/premium-landing-page",
  method: "GET",
  params,
  headers,
  ctx,
});

const getLandingPageContent = (params = {}, headers = {}, ctx = null) => hit({
  url: "/api/web/v1/static-content/landing-page",
  method: "GET",
  params,
  headers,
  ctx,
});

const getCollaborationContent = (params = {}, headers = {}, ctx = null) => hit({
  url: "/api/web/v1/static-content/collaboration-code",
  method: "GET",
  params,
  headers,
  ctx,
});

const getCollaborationVoucher = (params = {}, headers = {}, ctx = null) => hit({
  url: "api/v1/vouchers/collaborations",
  method: "GET",
  params,
  headers,
  ctx,
});

const getMeditationDetail = (workoutId) => hit({
  url: `/api/web/v1/meditation/${workoutId}`,
  method: "GET",
});

const getWorkoutDetail = (workoutId) => hit({
  url: `api/web/v1/workout/${workoutId}`,
  method: "GET",
});

const getProgramDetail = (programId) => hit({
  url: `api/web/v1/program/${programId}`,
  method: "GET",
});

const getFreemiumContent = (params, headers = {}) => hit({
  url: "/api/web/v1/static-content/premium-introduction",
  method: "GET",
  params,
  headers,
});

const challengeDetail = (challengeId, headers = {}) => hit({
  url: `api/web/v1/challenge/${challengeId}`,
  method: "GET",
  headers,
});

const participateChallenge = (challengeId, headers = {}) => hit({
  url: `api/v1/challenges/${challengeId}/participate`,
  method: "POST",
  headers,
});

const getChallengeWinner = (challengeId, headers = {}) => hit({
  url: `/api/web/v1/challenge/${challengeId}/winners`,
  method: "GET",
  headers,
});

const getBtnChallenge = (headers = {}) => hit({
  url: "/api/mobile/v1/homepage",
  method: "GET",
  headers,
});

const getPointContent = (params = {}) => hit({
  url: "api/web/v1/static-content/point-introduction",
  method: "GET",
  params,
});

const packageLists = (params = {}, headers = {}) => hit({
  url: "api/web/v1/product/workout/packages/",
  method: "GET",
  params,

  headers,
});

const loginWeb = (body = {}, params = {}, headers = {}) => hit({
  url: "api/web/v1/account/login",
  method: "POST",
  data: body,
  params,
  headers
});

const loginAffiliate = (body = {}, headers = {}) => hit({
  url: "api/web/v1/static-content/affiliate/authorize",
  method: "POST",
  data: body,
  headers,
});

const loginReseller = (body = {}, headers = {}) => hit({
  url: "api/web/v1/static-content/reseller/authorize",
  method: "POST",
  data: body,
  headers,
});

const registerWeb = (body = {}, params = {}, headers = {}) => hit({
  url: "api/web/v1/account/register",
  method: "POST",
  data: body,
  params,
  headers
});

const loginGoogle = (body = {}, headers) => hit({
  url: "api/web/v1/account/login-google",
  method: "POST",
  data: body,
  headers,
});

const loginFB = (body = {}) => hit({
  url: "api/web/v1/account/login-facebook",
  method: "POST",
  data: body,
});

const internalLogin = (body = {}) => hit({
  MAIN_URL: "/",
  url: "/api/login",
  method: "POST",
  data: body,
});

const forgotPassword = (body = {}, headers = {}) => hit({
  url: "oauth/password/forgot",
  method: "POST",
  data: body,
  headers,
});

const getProfile = (ctx = null, headers = {}) => hit({
  url: "oauth/member",
  method: "GET",
  ctx,
  headers,
});

// Trello 581 X1: bounded — a hung resend must fail fast, not after 20s.
const sendActivationEmail = (body = {}, headers = {}) => hit({
  url: "/oauth/activation/send",
  method: "POST",
  data: body,
  headers,
  timeout: OTP_UPSTREAM_TIMEOUT_MS,
});

const transactionDetail = (
  invoiceId = "",
  params = {},
  ctx,
  requestConfig = {}
) => hit({
  ...requestConfig,
  url: `api/v1/members/transactions/${invoiceId}`,
  method: "GET",
  params,
  ctx,
});

// Trello 581 X1: bounded — a hung code check must fail fast, not after 20s.
const webActivation = (body = {}, headers = {}) => hit({
  url: "oauth/activation/activate",
  method: "POST",
  data: body,
  headers,
  timeout: OTP_UPSTREAM_TIMEOUT_MS,
});

const completeRegisterData = (body = {}, headers = {}) => hit({
  url: "/api/v1/welcome/step1",
  method: "POST",
  data: body,
  headers,
});

const getActivePackage = (params = {}, headers) => hit({
  url: "api/v1/members/active-package",
  method: "GET",
  params,
  headers,
});

const businessInternalLogin = (body = {}) => hit({
  MAIN_URL: "/",
  url: "/api/business-login",
  method: "POST",
  data: body,
});

const getBusinessProfile = (params = {}, token) => hit({
  url: "/oauth/business",
  method: "GET",
  params,
  headers: token ? { Authorization: `Bearer ${token}` } : {},
});

const getBusinessPhoto = (userId, params = {}, ctx) => hit({
  url: `/api/v1/business/${userId}/photos`,
  method: "GET",
  params,
  ctx,
});

const businessInternalSetProfile = (body = {}) => hit({
  MAIN_URL: "/",
  url: "/api/business-profile",
  method: "POST",
  data: body,
});

// Trello 437: the business register response may carry `otp_length` — persist
// it so the business OTP page renders the backend's cell count.
const createBusinessAccount = (data) => hit({
  url: "/oauth/business",
  method: "POST",
  data,
}).then((resp) => { persistOtpLength(resp); return resp; });

const loginBusiness = (data) => hit({
  url: "/oauth/business/signin",
  method: "POST",
  data,
});

const getCookingVideos = (params = {}, cookie = {}) => hit({
  url: "/api/web/v1/cooking-video",
  method: "GET",
  params,
  headers: handleHeader(cookie),
});

const getOwnProfile = (token) => hit({
  url: "oauth/member",
  method: "GET",
  headers: token ? { Authorization: `Bearer ${token}` } : {},
});

const getPremiumStatus = (token, params, ctx = {}) => hit({
  url: "/api/v2/member/premium-status",
  method: "GET",
  headers: token ? { Authorization: `Bearer ${token}` } : {},
  params,
  ctx
});

const getListVoucher = (params = {}, headers = {}) => hit({
  url: "/api/v1/vouchers",
  method: "GET",
  params,
  headers,
});

const saveBusinessPhoto = (params = {}, data, userId) => hit({
  url: `/api/v1/business/${userId}/photos`,
  method: "POST",
  params,
  data,
});

const getBusinessDiscover = (ctx) => hit({
  url: "/api/v1/business/discover",
  method: "GET",
  ctx,
});

const getBusinessByCategory = (params, id) => hit({
  url: `/api/v1/business/get-type/${id}`,
  method: "GET",
  params,
});

const getBusinessCategory = () => hit({
  url: "/api/v1/business/type-options",
  method: "GET",
});

const getAllBusiness = (params) => hit({
  url: "/api/v1/business",
  method: "GET",
  params,
});

const getDetailBusiness = (id) => hit({
  url: `/api/v1/business/${id}`,
  method: "GET",
});

const getBusinessPhotoDetail = (id) => hit({
  url: `/api/v1/business-photos/${id}`,
  method: "GET",
});

const getPromoPackage = (id, ctx, headers = {}) => hit({
  url: `/api/v2/product/packages?promo_id=${id}`,
  method: "GET",
  ctx,
  headers,
});

const updateBusinessPhoto = (params = {}, data, photoId) => hit({
  url: `/api/v1/business-photos/${photoId}`,
  method: "POST",
  params,
  data,
});

const deleteBusinessPhoto = (params = {}, photoId) => hit({
  url: `/api/v1/business-photos/${photoId}`,
  method: "DELETE",
  params,
});

const sendActivationBusiness = (body = {}) => hit({
  url: "/oauth/activation/send",
  method: "POST",
  data: body,
});

// Trello 437: business activation / resend responses may carry `otp_length`.
const activationBusinessAccount = (body = {}) => hit({
  url: "/oauth/business/activation/activate",
  method: "POST",
  data: body,
  timeout: OTP_UPSTREAM_TIMEOUT_MS,
}).then((resp) => { persistOtpLength(resp); return resp; });

const resendEmailBusiness = (body = {}) => hit({
  url: "/oauth/business/activation/send",
  method: "POST",
  data: body,
  timeout: OTP_UPSTREAM_TIMEOUT_MS,
}).then((resp) => { persistOtpLength(resp); return resp; });

const updateBusinessAccount = (body = {}) => hit({
  url: "/oauth/business",
  method: "POST",
  data: body,
});

const updateBusinessSettings = (body = {}) => hit({
  url: "/oauth/business/settings",
  method: "PUT",
  data: body,
});

const midtransRecurring = (body = {}, headers = {}) => hit({
  url: "/api/v1/midtrans/recurring",
  method: "POST",
  data: body,
  headers,
});

const unsubscribePurchase = (body = {}, headers = {}) => hit({
  url: "/api/v1/members/subscription/cancel",
  method: "POST",
  data: body,
  headers,
});

const getForgeRedeem = (params = {}, headers = {}) => hit({
  url: "/api/web/v1/static-content/forge-redeem",
  method: "GET",
  params,
  headers,
});

const voucherRedeem = (body = {}, headers = {}) => hit({
  url: "/api/v1/vouchers/redeem",
  method: "POST",
  data: body,
  headers,
});

const setEmailPreference = (body = {}, headers = {}) => hit({
  url: `${process.env.API_V2}/oauth/member/email-preferences`,
  method: "POST",
  data: body,
  headers,
});

const setLanguage = (data, token, additionalHeaders = {}) => hit({
  url: "/oauth/member/set-language",
  method: "POST",
  data,
  headers: token ? { Authorization: `Bearer ${token}`, ...additionalHeaders } : { ...additionalHeaders },
});

const editProfile = (data, id, token) => hit({
  url: `/oauth/member/${id}`,
  method: "PUT",
  data,
  headers: token ? { Authorization: `Bearer ${token}` } : {},
});

const editProfileName = (data, token) => hit({
  url: "/api/v1/members/update-name",
  method: "POST",
  data,
  headers: token ? { Authorization: `Bearer ${token}` } : {},
});

const getAffiliateContent = (params = {}, headers = {}) => hit({
  url: "/api/web/v1/static-content/affiliate",
  method: "GET",
  params,
  headers,
});

const getResellerContent = (params = {}, headers = {}) => hit({
  url: "/api/web/v1/static-content/reseller",
  method: "GET",
  params,
  headers,
});

const checkProgramStatus = (params = {}, data = {}, headers = {}) => hit({
  url: "/api/v1/members/programs/customized-plan",
  method: "POST",
  params,
  data,
  headers,
});

const connectToGarmin = (body = {}, headers = {}) => hit({
  url: "/oauth/provider/garmin/token",
  method: "POST",
  data: body,
  headers,
});
const connectToStrava = (body = {}, headers = {}) => hit({
  url: "/oauth/provider/strava/token",
  method: "POST",
  data: body,
  headers,
});

const checkEmailUser = (body = {}, headers = {}) => hit({
  url: "/oauth/member/check-email",
  method: "POST",
  data: body,
  headers,
});

const updateUserQuestionnaire = (body = {}, headers = {}) => hit({
  url: "/api/web/v1/questionnaire/plan",
  method: "POST",
  data: body,
  headers,
});

const updateUserQuestionnaireV2 = (body = {}, headers = {}) => hit({
  url: "/api/web/v1/questionnaire-v2/plan",
  method: "POST",
  data: body,
  headers,
});

const createQuestionnaireFitTracker = (headers = {}) => hit({
  url: `${process.env.API_V2}/api/v3/welcome/questionnaire-fit-tracker-goal`,
  method: "POST",
  headers,
});

const checkProgramParticipation = (headers = {}, params = {}) => hit({
  url: "/api/v1/programs/check-participation",
  method: "PUT",
  headers,
  params,
});

const checkProgramParticipationNoToken = (params = {}, requestConfig = {}) => hit({
  ...requestConfig,
  url: "/api/v1/programs/check-participation",
  method: "PUT",
  params,
});

// Trello 546 (checklist 6): status read for the orchestrator's payment intent.
// Polled by /payment/insufficient/[intent_id] (through the
// /api/purchases/payment-intents proxy) while the buyer's e-wallet balance is
// short. Idempotent GET — callers may wrap it in retryIdempotentRead.
const getPaymentIntent = (id, params = {}, headers = {}, ctx = null, requestConfig = {}) => hit({
  ...requestConfig,
  url: `api/v1/members/payment-intents/${id}`,
  method: "GET",
  params,
  headers,
  ctx,
});

const checkLimitCollab = (params = {}, headers = {}) => hit({
  url: "/api/v1/landing-page-limit/check",
  method: "GET",
  headers,
  params,
});

const recordCmsPageVisit = (body) => hit({
  url: "api/web/v1/cms-page-visit",
  method: "POST",
  data: body,
});

// Trello SALES TRACKER (626): one funnel step for a referral code (no visitor data).
const recordReferralFunnelStep = (body) => hit({
  url: "api/web/v1/referral-funnel",
  method: "POST",
  data: body,
});

const sendMetaBrowserEvent = (body = {}, headers = {}) => hit({
  url: "/api/v1/meta/events",
  method: "POST",
  data: body,
  headers,
});


export {
  exchangeAppHandoffCode,
  getHome,
  getFitArticles,
  getArticleDetail,
  getContactUsCategories,
  createContact,
  getStaticContentDetail,
  activateUser,
  confirmChangeEmail,
  submitResetPassword,
  getArticleByCategories,
  getListFood,
  getDetailFood,
  subscribeNews,
  activateNewsLetter,
  unsubscribeNewsLetter,
  getPackageDetail,
  submitPromoCode,
  checkoutPackage,
  checkoutPackageXt,
  getProductPackages,
  likeArticle,
  checkWebsite,
  getShareContent,
  getWorkoutDetail,
  getProgramDetail,
  getFreemiumContent,
  challengeDetail,
  participateChallenge,
  getChallengeWinner,
  getBtnChallenge,
  getPointContent,
  getBusinessType,
  createBusinessAccount,
  loginBusiness,
  packageLists,
  loginWeb,
  registerWeb,
  loginGoogle,
  loginFB,
  internalLogin,
  forgotPassword,
  getProfile,
  sendActivationEmail,
  transactionDetail,
  webActivation,
  getActivePackage,
  getWorkoutLanding,
  businessInternalLogin,
  getBusinessProfile,
  businessInternalSetProfile,
  getBusinessPhoto,
  getCookingVideos,
  likeCookingVideos,
  getCookingDetail,
  getOwnProfile,
  getPremiumStatus,
  saveBusinessPhoto,
  getBusinessDiscover,
  deleteBusinessPhoto,
  getBusinessByCategory,
  getBusinessCategory,
  getAllBusiness,
  getDetailBusiness,
  getBusinessPhotoDetail,
  updateBusinessPhoto,
  sendActivationBusiness,
  activationBusinessAccount,
  updateBusinessAccount,
  resendEmailBusiness,
  midtransRecurring,
  getPromoPackage,
  getListVoucher,
  getForgeRedeem,
  voucherRedeem,
  setLanguage,
  editProfile,
  editProfileName,
  getAffiliateContent,
  updateBusinessSettings,
  connectToGarmin,
  completeRegisterData,
  loginAffiliate,
  getMeditationDetail,
  checkEmailUser,
  checkProgramStatus,
  updateUserQuestionnaire,
  updateUserQuestionnaireV2,
  checkProgramParticipation,
  loginReseller,
  getResellerContent,
  unsubscribePurchase,
  checkProgramParticipationNoToken,
  getCollaborationContent,
  getCollaborationVoucher,
  connectToStrava,
  setEmailPreference,
  getPaymentIntent,
  createQuestionnaireFitTracker,
  checkLimitCollab,
  getLandingPageContent,
  recordCmsPageVisit,
  recordReferralFunnelStep,
  sendMetaBrowserEvent
};
